import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Grid, Box, Typography, Tabs, Tab, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, TextField, MenuItem, CircularProgress, IconButton } from '@mui/material'
import { Edit, Print, QrCode2, Delete, Add, Download, ReportProblem, Visibility, CloudUpload, InsertDriveFile } from '@mui/icons-material'
import { PageHeader, Button, Card, StatusChip, LoadingSpinner, Modal } from '@components/common'
import { AssetForm, FaultReportForm } from '@components/assets'
import { WorkOrderForm } from '@components/maintenance'
import { useAuth } from '@context/AuthContext'
import api from '@services/api'
import { formatDate } from '@utils/dateUtils'
import { toast } from 'react-toastify'

const AssetDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const [asset, setAsset] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(0)
  const [editFormOpen, setEditFormOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [generatingQR, setGeneratingQR] = useState(false)
  const [faultReportFormOpen, setFaultReportFormOpen] = useState(false)

  // Work Orders state
  const [workOrders, setWorkOrders] = useState([])
  const [workOrdersLoading, setWorkOrdersLoading] = useState(false)

  // PM Schedules state
  const [pmSchedules, setPmSchedules] = useState([])
  const [pmSchedulesLoading, setPmSchedulesLoading] = useState(false)

  // Documents state
  const [documents, setDocuments] = useState([])
  const [documentsLoading, setDocumentsLoading] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadForm, setUploadForm] = useState({
    title: '',
    document_type: 'other',
    file: null
  })

  // Work Order Form state
  const [workOrderFormOpen, setWorkOrderFormOpen] = useState(false)

  // PM Schedule Form state
  const [pmFormOpen, setPmFormOpen] = useState(false)
  const [pmFormSaving, setPmFormSaving] = useState(false)
  const [pmTemplates, setPmTemplates] = useState([])
  const [pmTechnicians, setPmTechnicians] = useState([])
  const [selectedPmSchedule, setSelectedPmSchedule] = useState(null)
  const [pmFormData, setPmFormData] = useState({
    template: '',
    scheduled_date: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0],
    status: 'scheduled',
    assigned_to: '',
    maintenance_provider: 'internal',
    notes: '',
  })

  // Fault Reports state
  const [faultReports, setFaultReports] = useState([])
  const [faultReportsLoading, setFaultReportsLoading] = useState(false)

  // Transfers state
  const [transfers, setTransfers] = useState([])
  const [transfersLoading, setTransfersLoading] = useState(false)

  // Requisitions state
  const [requisitions, setRequisitions] = useState([])
  const [requisitionsLoading, setRequisitionsLoading] = useState(false)

  // Transfer Form state
  const [transferFormOpen, setTransferFormOpen] = useState(false)
  const [transferFormSaving, setTransferFormSaving] = useState(false)
  const [departments, setDepartments] = useState([])
  const [locations, setLocations] = useState([])
  const [transferFormData, setTransferFormData] = useState({
    to_department: '',
    to_location: '',
    reason: '',
    notes: '',
  })

  // Requisition Form state
  const [requisitionFormOpen, setRequisitionFormOpen] = useState(false)
  const [requisitionFormSaving, setRequisitionFormSaving] = useState(false)
  const [categories, setCategories] = useState([])
  const [requisitionFormData, setRequisitionFormData] = useState({
    requisition_type: 'swap',
    equipment_name: '',
    equipment_category: '',
    quantity: 1,
    department: '',
    location: '',
    priority: 'medium',
    justification: '',
    current_situation: '',
    estimated_cost: '',
    notes: '',
  })

  // Detail view state
  const [selectedFault, setSelectedFault] = useState(null)
  const [faultDetailOpen, setFaultDetailOpen] = useState(false)
  const [selectedTransfer, setSelectedTransfer] = useState(null)
  const [transferDetailOpen, setTransferDetailOpen] = useState(false)
  const [selectedRequisition, setSelectedRequisition] = useState(null)
  const [requisitionDetailOpen, setRequisitionDetailOpen] = useState(false)

  const fetchAsset = async () => {
    try {
      const response = await api.get(`/assets/assets/${id}/`)
      setAsset(response.data)
    } catch (error) {
      console.error('Error fetching asset:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAsset()
  }, [id])

  // Fetch work orders for this asset
  const fetchWorkOrders = async () => {
    setWorkOrdersLoading(true)
    try {
      const response = await api.get('/maintenance/work-orders/', {
        params: { asset: id, page_size: 100 }
      })
      setWorkOrders(response.data.results || response.data || [])
    } catch (error) {
      console.error('Error fetching work orders:', error)
      toast.error('Failed to load work orders')
    } finally {
      setWorkOrdersLoading(false)
    }
  }

  // Fetch PM schedules for this asset
  const fetchPmSchedules = async () => {
    setPmSchedulesLoading(true)
    try {
      const response = await api.get('/maintenance/pm-schedules/', {
        params: { asset: id, page_size: 100 }
      })
      setPmSchedules(response.data.results || response.data || [])
    } catch (error) {
      console.error('Error fetching PM schedules:', error)
      toast.error('Failed to load PM schedules')
    } finally {
      setPmSchedulesLoading(false)
    }
  }

  // Fetch documents for this asset
  const fetchDocuments = async () => {
    setDocumentsLoading(true)
    try {
      const response = await api.get('/assets/documents/', {
        params: { asset: id }
      })
      setDocuments(response.data.results || response.data || [])
    } catch (error) {
      console.error('Error fetching documents:', error)
      toast.error('Failed to load documents')
    } finally {
      setDocumentsLoading(false)
    }
  }

  // Fetch fault reports for this asset
  const fetchFaultReports = async () => {
    setFaultReportsLoading(true)
    try {
      const response = await api.get('/assets/fault-reports/', {
        params: { asset: id, page_size: 100 }
      })
      setFaultReports(response.data.results || response.data || [])
    } catch (error) {
      console.error('Error fetching fault reports:', error)
      toast.error('Failed to load fault reports')
    } finally {
      setFaultReportsLoading(false)
    }
  }

  // Fetch transfers for this asset
  const fetchTransfers = async () => {
    setTransfersLoading(true)
    try {
      const response = await api.get('/assets/transfers/', {
        params: { asset: id, page_size: 100 }
      })
      setTransfers(response.data.results || response.data || [])
    } catch (error) {
      console.error('Error fetching transfers:', error)
      toast.error('Failed to load transfer history')
    } finally {
      setTransfersLoading(false)
    }
  }

  // Fetch requisitions related to this asset
  const fetchRequisitions = async () => {
    setRequisitionsLoading(true)
    try {
      // Fetch requisitions where this asset is swap_asset or fulfilled_asset
      const [swapRes, fulfilledRes] = await Promise.all([
        api.get('/assets/requisitions/', { params: { swap_asset: id, page_size: 100 } }).catch(() => ({ data: [] })),
        api.get('/assets/requisitions/', { params: { fulfilled_asset: id, page_size: 100 } }).catch(() => ({ data: [] })),
      ])
      const swapReqs = swapRes.data.results || swapRes.data || []
      const fulfilledReqs = fulfilledRes.data.results || fulfilledRes.data || []
      // Combine and deduplicate
      const allReqs = [...swapReqs, ...fulfilledReqs]
      const uniqueReqs = allReqs.filter((req, index, self) =>
        index === self.findIndex(r => r.id === req.id)
      )
      setRequisitions(uniqueReqs)
    } catch (error) {
      console.error('Error fetching requisitions:', error)
      toast.error('Failed to load requisitions')
    } finally {
      setRequisitionsLoading(false)
    }
  }

  // Fetch tab data when tab changes
  useEffect(() => {
    if (activeTab === 1 && workOrders.length === 0 && !workOrdersLoading) {
      fetchWorkOrders()
    } else if (activeTab === 2 && pmSchedules.length === 0 && !pmSchedulesLoading) {
      fetchPmSchedules()
    } else if (activeTab === 3 && documents.length === 0 && !documentsLoading) {
      fetchDocuments()
    } else if (activeTab === 4 && faultReports.length === 0 && !faultReportsLoading) {
      fetchFaultReports()
    } else if (activeTab === 5 && transfers.length === 0 && !transfersLoading) {
      fetchTransfers()
    } else if (activeTab === 6 && requisitions.length === 0 && !requisitionsLoading) {
      fetchRequisitions()
    }
  }, [activeTab])

  // Handle document upload
  const handleDocumentUpload = async () => {
    if (!uploadForm.file || !uploadForm.title) {
      toast.error('Please fill in all required fields')
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('asset', id)
      formData.append('title', uploadForm.title)
      formData.append('document_type', uploadForm.document_type)
      formData.append('file', uploadForm.file)

      await api.post('/assets/documents/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      toast.success('Document uploaded successfully')
      setUploadModalOpen(false)
      setUploadForm({ title: '', document_type: 'other', file: null })
      fetchDocuments()
    } catch (error) {
      console.error('Error uploading document:', error)
      toast.error('Failed to upload document')
    } finally {
      setUploading(false)
    }
  }

  // Handle document delete
  const handleDocumentDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return

    try {
      await api.delete(`/assets/documents/${docId}/`)
      toast.success('Document deleted successfully')
      fetchDocuments()
    } catch (error) {
      console.error('Error deleting document:', error)
      toast.error('Failed to delete document')
    }
  }

  // Fetch PM form options
  const fetchPmFormOptions = async () => {
    try {
      const [templatesRes, techniciansRes] = await Promise.all([
        api.get('/maintenance/pm-templates/', { params: { is_active: true } }),
        api.get('/common/profiles/technicians/').catch(() => ({ data: [] })),
      ])
      setPmTemplates(templatesRes.data.results || templatesRes.data || [])
      setPmTechnicians(techniciansRes.data.results || techniciansRes.data || [])
    } catch (error) {
      console.error('Error fetching PM form options:', error)
    }
  }

  // Handle PM schedule save (create or update)
  const handlePmScheduleSave = async () => {
    if (!pmFormData.template) {
      toast.error('Please select a PM template')
      return
    }

    setPmFormSaving(true)
    try {
      const payload = {
        asset: id,
        template: pmFormData.template,
        scheduled_date: pmFormData.scheduled_date,
        due_date: pmFormData.due_date,
        status: pmFormData.status,
        assigned_to: pmFormData.assigned_to || null,
        maintenance_provider: pmFormData.maintenance_provider,
        notes: pmFormData.notes,
      }

      if (selectedPmSchedule) {
        await api.patch(`/maintenance/pm-schedules/${selectedPmSchedule.id}/`, payload)
        toast.success('PM schedule updated successfully')
      } else {
        await api.post('/maintenance/pm-schedules/', payload)
        toast.success('PM schedule created successfully')
      }

      setPmFormOpen(false)
      setSelectedPmSchedule(null)
      setPmFormData({
        template: '',
        scheduled_date: new Date().toISOString().split('T')[0],
        due_date: new Date().toISOString().split('T')[0],
        status: 'scheduled',
        assigned_to: '',
        maintenance_provider: 'internal',
        notes: '',
      })
      fetchPmSchedules()
    } catch (error) {
      console.error('Error saving PM schedule:', error)
      toast.error(selectedPmSchedule ? 'Failed to update PM schedule' : 'Failed to create PM schedule')
    } finally {
      setPmFormSaving(false)
    }
  }

  const openPmForm = (schedule = null) => {
    fetchPmFormOptions()
    setSelectedPmSchedule(schedule)
    if (schedule) {
      // Edit mode - populate form with existing data
      setPmFormData({
        template: schedule.template?.id || schedule.template || '',
        scheduled_date: schedule.scheduled_date || new Date().toISOString().split('T')[0],
        due_date: schedule.due_date || new Date().toISOString().split('T')[0],
        status: schedule.status || 'scheduled',
        assigned_to: schedule.assigned_to?.id || schedule.assigned_to || '',
        maintenance_provider: schedule.maintenance_provider || 'internal',
        notes: schedule.notes || '',
      })
    } else {
      // Create mode - reset form
      setPmFormData({
        template: '',
        scheduled_date: new Date().toISOString().split('T')[0],
        due_date: new Date().toISOString().split('T')[0],
        status: 'scheduled',
        assigned_to: '',
        maintenance_provider: 'internal',
        notes: '',
      })
    }
    setPmFormOpen(true)
  }

  // Fetch transfer form options
  const fetchTransferFormOptions = async () => {
    try {
      const [deptsRes, locsRes] = await Promise.all([
        api.get('/common/departments/'),
        api.get('/common/locations/'),
      ])
      setDepartments(deptsRes.data.results || deptsRes.data || [])
      setLocations(locsRes.data.results || locsRes.data || [])
    } catch (error) {
      console.error('Error fetching transfer form options:', error)
    }
  }

  // Open transfer form
  const openTransferForm = () => {
    fetchTransferFormOptions()
    setTransferFormData({
      to_department: '',
      to_location: '',
      reason: '',
      notes: '',
    })
    setTransferFormOpen(true)
  }

  // Handle transfer request creation
  const handleTransferCreate = async () => {
    if (!transferFormData.to_department) {
      toast.error('Please select a destination department')
      return
    }

    setTransferFormSaving(true)
    try {
      await api.post('/assets/transfers/', {
        asset: id,
        to_department: transferFormData.to_department,
        to_location: transferFormData.to_location || null,
        reason: transferFormData.reason,
        notes: transferFormData.notes,
      })

      toast.success('Transfer request created successfully')
      setTransferFormOpen(false)
      fetchTransfers()
    } catch (error) {
      console.error('Error creating transfer:', error)
      toast.error('Failed to create transfer request')
    } finally {
      setTransferFormSaving(false)
    }
  }

  // Fetch requisition form options
  const fetchRequisitionFormOptions = async () => {
    try {
      const [deptsRes, locsRes, catsRes] = await Promise.all([
        api.get('/common/departments/'),
        api.get('/common/locations/'),
        api.get('/assets/categories/'),
      ])
      setDepartments(deptsRes.data.results || deptsRes.data || [])
      setLocations(locsRes.data.results || locsRes.data || [])
      setCategories(catsRes.data.results || catsRes.data || [])
    } catch (error) {
      console.error('Error fetching requisition form options:', error)
    }
  }

  // Open requisition form
  const openRequisitionForm = () => {
    fetchRequisitionFormOptions()
    setRequisitionFormData({
      requisition_type: 'swap',
      equipment_name: asset?.name || '',
      equipment_category: asset?.category?.id || '',
      quantity: 1,
      department: asset?.department || '',
      location: asset?.location?.id || '',
      priority: 'medium',
      justification: '',
      current_situation: '',
      estimated_cost: '',
      notes: '',
    })
    setRequisitionFormOpen(true)
  }

  // Handle requisition creation
  const handleRequisitionCreate = async () => {
    if (!requisitionFormData.equipment_name || !requisitionFormData.department) {
      toast.error('Please fill in required fields')
      return
    }

    setRequisitionFormSaving(true)
    try {
      await api.post('/assets/requisitions/', {
        ...requisitionFormData,
        swap_asset: id,
        equipment_category: requisitionFormData.equipment_category || null,
        location: requisitionFormData.location || null,
        estimated_cost: requisitionFormData.estimated_cost || null,
      })

      toast.success('Requisition created successfully')
      setRequisitionFormOpen(false)
      fetchRequisitions()
    } catch (error) {
      console.error('Error creating requisition:', error)
      toast.error('Failed to create requisition')
    } finally {
      setRequisitionFormSaving(false)
    }
  }

  // View fault report detail
  const viewFaultDetail = (fault) => {
    setSelectedFault(fault)
    setFaultDetailOpen(true)
  }

  // View transfer detail
  const viewTransferDetail = (transfer) => {
    setSelectedTransfer(transfer)
    setTransferDetailOpen(true)
  }

  // View requisition detail
  const viewRequisitionDetail = (req) => {
    setSelectedRequisition(req)
    setRequisitionDetailOpen(true)
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.delete(`/assets/assets/${id}/`)
      navigate('/assets')
    } catch (error) {
      console.error('Error deleting asset:', error)
    } finally {
      setDeleting(false)
    }
  }

  const handleEditSuccess = () => {
    fetchAsset()
  }

  const handlePrint = () => {
    navigate(`/assets/${id}/print`)
  }

  const handleDownloadQR = () => {
    if (asset.qr_code) {
      const link = document.createElement('a')
      link.href = asset.qr_code
      link.download = `${asset.asset_tag || asset.name}_qr_code.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleGenerateQR = async () => {
    setGeneratingQR(true)
    try {
      const response = await api.post(`/assets/assets/${id}/generate_qr_code/`)
      setAsset(response.data)
    } catch (error) {
      console.error('Error generating QR code:', error)
    } finally {
      setGeneratingQR(false)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading asset details..." />
  }

  if (!asset) {
    return (
      <Box textAlign="center" py={4}>
        <Typography>Asset not found</Typography>
        <Button onClick={() => navigate('/assets')} sx={{ mt: 2 }}>
          Back to Assets
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title={asset.name}
        subtitle={`Asset Tag: ${asset.asset_tag || 'N/A'}`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets', path: '/assets' },
          { label: asset.name },
        ]}
        actions={
          <>
            {hasPermission('fault_reports.create') && (
              <Button
                variant="contained"
                color="error"
                startIcon={<ReportProblem />}
                onClick={() => setFaultReportFormOpen(true)}
              >
                Report Fault
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<QrCode2 />}
              onClick={() => setQrModalOpen(true)}
            >
              QR Code
            </Button>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={handlePrint}
            >
              Print
            </Button>
            {hasPermission('assets.edit') && (
              <Button
                startIcon={<Edit />}
                onClick={() => setEditFormOpen(true)}
              >
                Edit
              </Button>
            )}
            {hasPermission('assets.delete') && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => setDeleteModalOpen(true)}
              >
                Delete
              </Button>
            )}
          </>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <Box mb={2}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
              >
                <Tab label="Details" />
                <Tab label="Work Orders" />
                <Tab label="PM Schedule" />
                <Tab label="Documents" />
                <Tab label="Fault Reports" />
                <Tab label="Transfers" />
                <Tab label="Requisitions" />
              </Tabs>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {activeTab === 0 && (
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    Basic Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box mt={0.5}>
                    <StatusChip status={asset.status} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Criticality
                  </Typography>
                  <Box mt={0.5}>
                    <StatusChip status={asset.criticality} type="criticality" />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.category?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Section/Unit
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.department_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Facility
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.facility_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Number of Units
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.unit_count || 1}
                  </Typography>
                </Grid>

                {/* Technical Details */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                    Technical Details
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Manufacturer
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.manufacturer?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Model Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.model_number || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Serial Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.serial_number || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Engraved No / Asset Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.engraved_no || '-'}
                  </Typography>
                </Grid>

                {/* Location */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                    Current Location
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Department/Unit
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.department_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Facility
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.facility_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Location/Area
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.location?.name || asset.location_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Room Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.room_number || '-'}
                  </Typography>
                </Grid>

                {/* Purchase & Warranty */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                    Purchase & Warranty
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Purchase Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.purchase_date || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Purchase Cost
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.purchase_cost ? `UGX ${Number(asset.purchase_cost).toLocaleString()}` : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Warranty Expiry
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.warranty_expiry || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Expected Life
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.expected_life_years ? `${asset.expected_life_years} years` : '-'}
                  </Typography>
                </Grid>

                {/* Manufacturing & Origin */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                    Manufacturing & Origin
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Manufacturing Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.manufacturing_date || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Country of Origin
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.country_of_origin || '-'}
                  </Typography>
                </Grid>

                {/* Receiving Information */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                    Receiving Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Supplier
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.supplier_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Delivered By
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.delivered_by || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Received By
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.received_by_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Received Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.received_date || '-'}
                  </Typography>
                </Grid>

                {/* Remarks */}
                {asset.notes && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                        Remarks
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        {asset.notes}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            )}

            {activeTab === 1 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Work Orders</Typography>
                  {hasPermission('work_orders.create') && (
                    <Button size="small" startIcon={<Add />} onClick={() => setWorkOrderFormOpen(true)}>
                      Create Work Order
                    </Button>
                  )}
                </Box>
                {workOrdersLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={32} />
                  </Box>
                ) : workOrders.length === 0 ? (
                  <Typography color="text.secondary">
                    No work orders found for this asset
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>WO Number</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Priority</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {workOrders.map((wo) => (
                          <TableRow key={wo.id} hover>
                            <TableCell>{wo.work_order_number}</TableCell>
                            <TableCell>{wo.title}</TableCell>
                            <TableCell>
                              <StatusChip status={wo.status} size="small" />
                            </TableCell>
                            <TableCell>
                              <StatusChip status={wo.priority} type="priority" size="small" />
                            </TableCell>
                            <TableCell>{formatDate(wo.created_at)}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => navigate(`/work-orders/${wo.id}`)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">PM Schedule</Typography>
                  {hasPermission('pm.create') && (
                    <Button size="small" startIcon={<Add />} onClick={openPmForm}>
                      Add PM Schedule
                    </Button>
                  )}
                </Box>
                {pmSchedulesLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={32} />
                  </Box>
                ) : pmSchedules.length === 0 ? (
                  <Typography color="text.secondary">
                    No preventive maintenance scheduled for this asset
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Template</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Scheduled Date</TableCell>
                          <TableCell>Due Date</TableCell>
                          <TableCell>Assigned To</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pmSchedules.map((pm) => (
                          <TableRow key={pm.id} hover>
                            <TableCell>{pm.template_name || pm.template?.name || 'PM Task'}</TableCell>
                            <TableCell>
                              <StatusChip status={pm.status} size="small" />
                            </TableCell>
                            <TableCell>{formatDate(pm.scheduled_date)}</TableCell>
                            <TableCell>{formatDate(pm.due_date)}</TableCell>
                            <TableCell>{pm.assigned_to_name || pm.assigned_to?.username || 'Unassigned'}</TableCell>
                            <TableCell align="right">
                              {hasPermission('pm.edit') && (
                                <IconButton size="small" onClick={() => openPmForm(pm)} title="View/Edit">
                                  <Edit fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Documents</Typography>
                  {hasPermission('assets.edit') && (
                    <Button size="small" startIcon={<Add />} onClick={() => setUploadModalOpen(true)}>
                      Upload Document
                    </Button>
                  )}
                </Box>
                {documentsLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={32} />
                  </Box>
                ) : documents.length === 0 ? (
                  <Typography color="text.secondary">
                    No documents uploaded for this asset
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Title</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Uploaded By</TableCell>
                          <TableCell>Uploaded At</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {documents.map((doc) => (
                          <TableRow key={doc.id} hover>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <InsertDriveFile fontSize="small" color="action" />
                                {doc.title}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={doc.document_type?.replace('_', ' ') || 'Other'}
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </TableCell>
                            <TableCell>{doc.uploaded_by_name || '-'}</TableCell>
                            <TableCell>{formatDate(doc.uploaded_at)}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => window.open(doc.file, '_blank')}
                                title="Download"
                              >
                                <Download fontSize="small" />
                              </IconButton>
                              {hasPermission('assets.edit') && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleDocumentDelete(doc.id)}
                                  title="Delete"
                                  color="error"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Fault Reports Tab */}
            {activeTab === 4 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Fault Reports</Typography>
                  {hasPermission('fault_reports.create') && (
                    <Button size="small" startIcon={<Add />} onClick={() => setFaultReportFormOpen(true)}>
                      Report Fault
                    </Button>
                  )}
                </Box>
                {faultReportsLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={32} />
                  </Box>
                ) : faultReports.length === 0 ? (
                  <Typography color="text.secondary">
                    No fault reports for this asset
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Report #</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell>Priority</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Reported</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {faultReports.map((fault) => (
                          <TableRow key={fault.id} hover>
                            <TableCell>{fault.report_number}</TableCell>
                            <TableCell>{fault.title}</TableCell>
                            <TableCell>
                              <StatusChip status={fault.priority} type="priority" size="small" />
                            </TableCell>
                            <TableCell>
                              <StatusChip status={fault.status} size="small" />
                            </TableCell>
                            <TableCell>{formatDate(fault.reported_at)}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => viewFaultDetail(fault)} title="View Details">
                                <Visibility fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Transfers Tab */}
            {activeTab === 5 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Transfer History</Typography>
                  {hasPermission('transfers.request') && (
                    <Button size="small" startIcon={<Add />} onClick={openTransferForm}>
                      Request Transfer
                    </Button>
                  )}
                </Box>
                {transfersLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={32} />
                  </Box>
                ) : transfers.length === 0 ? (
                  <Typography color="text.secondary">
                    No transfer history for this asset
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Transfer #</TableCell>
                          <TableCell>From</TableCell>
                          <TableCell>To</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transfers.map((transfer) => (
                          <TableRow key={transfer.id} hover>
                            <TableCell>{transfer.transfer_number}</TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                                {transfer.from_department_name || transfer.from_location_name || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                                {transfer.to_department_name || transfer.to_location_name || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <StatusChip status={transfer.status} size="small" />
                            </TableCell>
                            <TableCell>{formatDate(transfer.requested_date || transfer.created_at)}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => viewTransferDetail(transfer)} title="View Details">
                                <Visibility fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Requisitions Tab */}
            {activeTab === 6 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Requisitions</Typography>
                  {hasPermission('requisitions.create') && (
                    <Button size="small" startIcon={<Add />} onClick={openRequisitionForm}>
                      New Requisition
                    </Button>
                  )}
                </Box>
                {requisitionsLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={32} />
                  </Box>
                ) : requisitions.length === 0 ? (
                  <Typography color="text.secondary">
                    No requisitions related to this asset
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Requisition #</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Equipment</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {requisitions.map((req) => (
                          <TableRow key={req.id} hover>
                            <TableCell>{req.requisition_number}</TableCell>
                            <TableCell>
                              <Chip
                                label={req.requisition_type?.replace('_', ' ') || 'N/A'}
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </TableCell>
                            <TableCell>{req.equipment_name || '-'}</TableCell>
                            <TableCell>
                              <StatusChip status={req.status} size="small" />
                            </TableCell>
                            <TableCell>{formatDate(req.created_at)}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => viewRequisitionDetail(req)} title="View Details">
                                <Visibility fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Asset Images */}
          {(asset.image || asset.image_2 || asset.image_3 || asset.qr_code) && (
            <Card title="Images" sx={{ mb: 3 }}>
              <Box display="flex" flexDirection="column" gap={2}>
                {asset.image && (
                  <Box
                    component="img"
                    src={asset.image}
                    alt={asset.name}
                    sx={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 1,
                      bgcolor: 'grey.100',
                    }}
                  />
                )}
                <Box display="flex" gap={1} flexWrap="wrap">
                  {asset.image_2 && (
                    <Box
                      component="img"
                      src={asset.image_2}
                      alt={`${asset.name} - 2`}
                      sx={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  )}
                  {asset.image_3 && (
                    <Box
                      component="img"
                      src={asset.image_3}
                      alt={`${asset.name} - 3`}
                      sx={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  )}
                  {asset.qr_code && (
                    <Box
                      component="img"
                      src={asset.qr_code}
                      alt="QR Code"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'contain',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'white',
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Card>
          )}

          <Card title="Quick Info">
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Asset Tag
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {asset.asset_tag || 'Not assigned'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created By
                </Typography>
                <Typography variant="body2">
                  {asset.created_by_name || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body2">
                  {formatDate(asset.created_at)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body2">
                  {formatDate(asset.updated_at)}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <AssetForm
        open={editFormOpen}
        onClose={() => setEditFormOpen(false)}
        asset={asset}
        onSuccess={handleEditSuccess}
      />

      <FaultReportForm
        open={faultReportFormOpen}
        onClose={() => setFaultReportFormOpen(false)}
        asset={asset}
        onSuccess={() => {
          setFaultReportFormOpen(false)
          fetchFaultReports()
          toast.success('Fault report submitted successfully')
        }}
      />

      <WorkOrderForm
        open={workOrderFormOpen}
        onClose={() => setWorkOrderFormOpen(false)}
        defaultAssetId={id}
        onSuccess={() => {
          setWorkOrderFormOpen(false)
          fetchWorkOrders()
          toast.success('Work order created successfully')
        }}
      />

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Asset"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        <Typography>
          Are you sure you want to delete "{asset.name}"? This action cannot be undone.
        </Typography>
      </Modal>

      <Modal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        title="Asset QR Code"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setQrModalOpen(false)}>
              Close
            </Button>
            {asset.qr_code ? (
              <Button startIcon={<Download />} onClick={handleDownloadQR}>
                Download
              </Button>
            ) : (
              <Button onClick={handleGenerateQR} loading={generatingQR}>
                Generate QR Code
              </Button>
            )}
          </>
        }
      >
        <Box display="flex" flexDirection="column" alignItems="center" py={2}>
          {asset.qr_code ? (
            <>
              <Box
                component="img"
                src={asset.qr_code}
                alt="Asset QR Code"
                sx={{
                  width: 200,
                  height: 200,
                  objectFit: 'contain',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'white',
                  p: 1,
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {asset.asset_tag || asset.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Scan to view asset details
              </Typography>
            </>
          ) : (
            <Box textAlign="center">
              <QrCode2 sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                No QR code has been generated for this asset yet.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click "Generate QR Code" to create one.
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Document Upload Modal */}
      <Modal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        title="Upload Document"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setUploadModalOpen(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleDocumentUpload} loading={uploading} startIcon={<CloudUpload />}>
              Upload
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2} py={1}>
          <TextField
            label="Document Title"
            value={uploadForm.title}
            onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
            fullWidth
            required
          />
          <TextField
            select
            label="Document Type"
            value={uploadForm.document_type}
            onChange={(e) => setUploadForm({ ...uploadForm, document_type: e.target.value })}
            fullWidth
          >
            <MenuItem value="manual">Manual</MenuItem>
            <MenuItem value="warranty">Warranty</MenuItem>
            <MenuItem value="certificate">Certificate</MenuItem>
            <MenuItem value="calibration">Calibration Certificate</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <Box>
            <input
              type="file"
              id="document-upload"
              style={{ display: 'none' }}
              onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
            />
            <label htmlFor="document-upload">
              <Button variant="outlined" component="span" startIcon={<CloudUpload />} fullWidth>
                {uploadForm.file ? uploadForm.file.name : 'Select File'}
              </Button>
            </label>
          </Box>
        </Box>
      </Modal>

      {/* PM Schedule Form Modal */}
      <Modal
        open={pmFormOpen}
        onClose={() => { setPmFormOpen(false); setSelectedPmSchedule(null); }}
        title={selectedPmSchedule ? 'Edit PM Schedule' : 'Add PM Schedule'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => { setPmFormOpen(false); setSelectedPmSchedule(null); }} disabled={pmFormSaving}>
              Cancel
            </Button>
            <Button onClick={handlePmScheduleSave} loading={pmFormSaving}>
              {selectedPmSchedule ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2} py={1}>
          <TextField
            select
            label="PM Template"
            value={pmFormData.template}
            onChange={(e) => setPmFormData({ ...pmFormData, template: e.target.value })}
            fullWidth
            required
          >
            {pmTemplates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            label="Scheduled Date"
            value={pmFormData.scheduled_date}
            onChange={(e) => setPmFormData({ ...pmFormData, scheduled_date: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="Due Date"
            value={pmFormData.due_date}
            onChange={(e) => setPmFormData({ ...pmFormData, due_date: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          {selectedPmSchedule && (
            <TextField
              select
              label="Status"
              value={pmFormData.status}
              onChange={(e) => setPmFormData({ ...pmFormData, status: e.target.value })}
              fullWidth
            >
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="skipped">Skipped</MenuItem>
            </TextField>
          )}
          <TextField
            select
            label="Assigned To"
            value={pmFormData.assigned_to}
            onChange={(e) => setPmFormData({ ...pmFormData, assigned_to: e.target.value })}
            fullWidth
          >
            <MenuItem value="">Unassigned</MenuItem>
            {pmTechnicians.map((tech) => (
              <MenuItem key={tech.id} value={tech.user?.id || tech.id}>
                {tech.user?.first_name && tech.user?.last_name
                  ? `${tech.user.first_name} ${tech.user.last_name}`
                  : tech.user?.username || tech.username || 'Unknown'}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Maintenance Provider"
            value={pmFormData.maintenance_provider}
            onChange={(e) => setPmFormData({ ...pmFormData, maintenance_provider: e.target.value })}
            fullWidth
          >
            <MenuItem value="internal">Internal (In-house BME)</MenuItem>
            <MenuItem value="external">External (Vendor)</MenuItem>
          </TextField>
          <TextField
            label="Notes"
            value={pmFormData.notes}
            onChange={(e) => setPmFormData({ ...pmFormData, notes: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
        </Box>
      </Modal>

      {/* Transfer Request Form Modal */}
      <Modal
        open={transferFormOpen}
        onClose={() => setTransferFormOpen(false)}
        title="Request Asset Transfer"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setTransferFormOpen(false)} disabled={transferFormSaving}>
              Cancel
            </Button>
            <Button onClick={handleTransferCreate} loading={transferFormSaving}>
              Submit Request
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2} py={1}>
          <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Transferring: <strong>{asset?.name}</strong> ({asset?.asset_tag})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Current Location: {asset?.department_name || 'N/A'}
            </Typography>
          </Box>
          <TextField
            select
            label="Destination Department"
            value={transferFormData.to_department}
            onChange={(e) => setTransferFormData({ ...transferFormData, to_department: e.target.value })}
            fullWidth
            required
          >
            {departments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Destination Location (Optional)"
            value={transferFormData.to_location}
            onChange={(e) => setTransferFormData({ ...transferFormData, to_location: e.target.value })}
            fullWidth
          >
            <MenuItem value="">None</MenuItem>
            {locations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Reason for Transfer"
            value={transferFormData.reason}
            onChange={(e) => setTransferFormData({ ...transferFormData, reason: e.target.value })}
            fullWidth
            multiline
            rows={2}
            required
          />
          <TextField
            label="Additional Notes"
            value={transferFormData.notes}
            onChange={(e) => setTransferFormData({ ...transferFormData, notes: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
        </Box>
      </Modal>

      {/* Requisition Form Modal */}
      <Modal
        open={requisitionFormOpen}
        onClose={() => setRequisitionFormOpen(false)}
        title="New Equipment Requisition"
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setRequisitionFormOpen(false)} disabled={requisitionFormSaving}>
              Cancel
            </Button>
            <Button onClick={handleRequisitionCreate} loading={requisitionFormSaving}>
              Submit Requisition
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2} py={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Requisition Type"
                value={requisitionFormData.requisition_type}
                onChange={(e) => setRequisitionFormData({ ...requisitionFormData, requisition_type: e.target.value })}
                fullWidth
              >
                <MenuItem value="swap">Swap/Replace</MenuItem>
                <MenuItem value="new_equipment">New Equipment</MenuItem>
                <MenuItem value="replacement">Replacement</MenuItem>
                <MenuItem value="additional">Additional</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Priority"
                value={requisitionFormData.priority}
                onChange={(e) => setRequisitionFormData({ ...requisitionFormData, priority: e.target.value })}
                fullWidth
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label="Equipment Name"
                value={requisitionFormData.equipment_name}
                onChange={(e) => setRequisitionFormData({ ...requisitionFormData, equipment_name: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                type="number"
                label="Quantity"
                value={requisitionFormData.quantity}
                onChange={(e) => setRequisitionFormData({ ...requisitionFormData, quantity: parseInt(e.target.value) || 1 })}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Equipment Category"
                value={requisitionFormData.equipment_category}
                onChange={(e) => setRequisitionFormData({ ...requisitionFormData, equipment_category: e.target.value })}
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Estimated Cost"
                type="number"
                value={requisitionFormData.estimated_cost}
                onChange={(e) => setRequisitionFormData({ ...requisitionFormData, estimated_cost: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Department"
                value={requisitionFormData.department}
                onChange={(e) => setRequisitionFormData({ ...requisitionFormData, department: e.target.value })}
                fullWidth
                required
              >
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Location"
                value={requisitionFormData.location}
                onChange={(e) => setRequisitionFormData({ ...requisitionFormData, location: e.target.value })}
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Justification"
                value={requisitionFormData.justification}
                onChange={(e) => setRequisitionFormData({ ...requisitionFormData, justification: e.target.value })}
                fullWidth
                multiline
                rows={2}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Current Situation"
                value={requisitionFormData.current_situation}
                onChange={(e) => setRequisitionFormData({ ...requisitionFormData, current_situation: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={requisitionFormData.notes}
                onChange={(e) => setRequisitionFormData({ ...requisitionFormData, notes: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Box>
      </Modal>

      {/* Fault Report Detail Modal */}
      <Modal
        open={faultDetailOpen}
        onClose={() => { setFaultDetailOpen(false); setSelectedFault(null); }}
        title={`Fault Report: ${selectedFault?.report_number || ''}`}
        maxWidth="sm"
        actions={
          <Button variant="outlined" onClick={() => { setFaultDetailOpen(false); setSelectedFault(null); }}>
            Close
          </Button>
        }
      >
        {selectedFault && (
          <Box display="flex" flexDirection="column" gap={2} py={1}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Title</Typography>
                <Typography variant="body2">{selectedFault.title}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box><StatusChip status={selectedFault.status} size="small" /></Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Priority</Typography>
                <Box><StatusChip status={selectedFault.priority} type="priority" size="small" /></Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Fault Type</Typography>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{selectedFault.fault_type?.replace('_', ' ') || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Reported By</Typography>
                <Typography variant="body2">{selectedFault.reported_by_name || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Reported At</Typography>
                <Typography variant="body2">{formatDate(selectedFault.reported_at)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body2">{selectedFault.description || '-'}</Typography>
              </Grid>
              {selectedFault.work_order_number && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Work Order</Typography>
                  <Typography variant="body2">{selectedFault.work_order_number}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Modal>

      {/* Transfer Detail Modal */}
      <Modal
        open={transferDetailOpen}
        onClose={() => { setTransferDetailOpen(false); setSelectedTransfer(null); }}
        title={`Transfer: ${selectedTransfer?.transfer_number || ''}`}
        maxWidth="sm"
        actions={
          <Button variant="outlined" onClick={() => { setTransferDetailOpen(false); setSelectedTransfer(null); }}>
            Close
          </Button>
        }
      >
        {selectedTransfer && (
          <Box display="flex" flexDirection="column" gap={2} py={1}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box><StatusChip status={selectedTransfer.status} size="small" /></Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Requested Date</Typography>
                <Typography variant="body2">{formatDate(selectedTransfer.requested_date)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">From Department</Typography>
                <Typography variant="body2">{selectedTransfer.from_department_name || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">To Department</Typography>
                <Typography variant="body2">{selectedTransfer.to_department_name || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">From Location</Typography>
                <Typography variant="body2">{selectedTransfer.from_location_name || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">To Location</Typography>
                <Typography variant="body2">{selectedTransfer.to_location_name || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Requested By</Typography>
                <Typography variant="body2">{selectedTransfer.requested_by_name || '-'}</Typography>
              </Grid>
              {selectedTransfer.approved_by_name && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Approved By</Typography>
                  <Typography variant="body2">{selectedTransfer.approved_by_name}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Reason</Typography>
                <Typography variant="body2">{selectedTransfer.reason || '-'}</Typography>
              </Grid>
              {selectedTransfer.notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography variant="body2">{selectedTransfer.notes}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Modal>

      {/* Requisition Detail Modal */}
      <Modal
        open={requisitionDetailOpen}
        onClose={() => { setRequisitionDetailOpen(false); setSelectedRequisition(null); }}
        title={`Requisition: ${selectedRequisition?.requisition_number || ''}`}
        maxWidth="sm"
        actions={
          <Button variant="outlined" onClick={() => { setRequisitionDetailOpen(false); setSelectedRequisition(null); }}>
            Close
          </Button>
        }
      >
        {selectedRequisition && (
          <Box display="flex" flexDirection="column" gap={2} py={1}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{selectedRequisition.requisition_type?.replace('_', ' ') || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box><StatusChip status={selectedRequisition.status} size="small" /></Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Priority</Typography>
                <Box><StatusChip status={selectedRequisition.priority} type="priority" size="small" /></Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Quantity</Typography>
                <Typography variant="body2">{selectedRequisition.quantity || 1}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Equipment Name</Typography>
                <Typography variant="body2">{selectedRequisition.equipment_name || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Department</Typography>
                <Typography variant="body2">{selectedRequisition.department_name || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Requested By</Typography>
                <Typography variant="body2">{selectedRequisition.requested_by_name || '-'}</Typography>
              </Grid>
              {selectedRequisition.estimated_cost && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Estimated Cost</Typography>
                  <Typography variant="body2">UGX {Number(selectedRequisition.estimated_cost).toLocaleString()}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Justification</Typography>
                <Typography variant="body2">{selectedRequisition.justification || '-'}</Typography>
              </Grid>
              {selectedRequisition.current_situation && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Current Situation</Typography>
                  <Typography variant="body2">{selectedRequisition.current_situation}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Modal>
    </Box>
  )
}

export default AssetDetail
